// MACHINERY V2 (Stroje) — V3 BELOW 1024: a dense LIST, not a table (M2.16b).
//
// Rendered truth: mobile/screenshots/10-v3-phone.png, viewed before this file
// was written. Reference impl: mobile/machinery-mobile.jsx MbV3Phone (:294-328).
//
// Composition: the ZORADIŤ chip row (FleetSortChips) · one card per group ·
// the footnote naming the width where the columns appear.
//
// >>> IT IS NOT ONLY THE PHONE ANY MORE (review finding F5). <<< The
// list/table boundary moved from 768 to 1024, so this component also serves the
// small-tablet band 768-1023, where the seven-column table's fixed widths alone
// (666px) leave a ~740px content box nothing to give the machine name. The FILE
// NAME is historical and kept deliberately: renaming it would mean deleting a
// file in a pass whose rule is additive-only. Read "Phone" here as "the dense
// tier".
//
// ============================================================
//  >>> THE GROUP SECTIONS STAY. THE BOARD DROPS THEM; WE DO NOT. <<<
// ============================================================
// Boards 10 and 11 both draw V3 as ONE flat list with no section headers. That
// is a MOCK ARTEFACT and must not be copied back in. Three things break if the
// sections go below 1280, and none of them is cosmetic:
//   1. THE COLLAPSE STATE IS SHARED. `collapsedGroups` is keyed on the RAW group
//      key and is the SAME set V1's cards and V2's rows use
//      (useFleetSectionState.ts). A flat V3 has nothing to apply it to, so a
//      section collapsed in V1 would silently re-expand on a view toggle.
//   2. SORTING IS PER SECTION, because each section is its own sorted list
//      (FleetTableView.tsx:104-111 — the memo, re-read while writing this line;
//      the header rewrite shifted it again). Flatten the view and "Stroj ↑"
//      would have to mean something different here from what it means one
//      breakpoint up.
//   3. The group HEALTH counts (FleetGroupHeader) are the only per-group
//      summary the phone has at all — the desktop rail is gone below 1280.
// The header itself is FleetGroupHeader, i.e. the same component V2's rows and
// the 1024 table use, so the phone cannot section the fleet differently from any
// other surface. >>> DO NOT "fix" this back to the PNG. <<<
//
// SORTING IS DONE UPSTREAM. FleetTableView memoises `sortMachines` per section
// and hands the result here, so the phone list and the tablet table are ordered
// by literally the same call — one comparator, one memo, one poller-friendly
// dependency list.
//
// ONE DERIVATION PER MACHINE, made here and handed to the row. Nothing below
// re-tests raw telemetry.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import { Machine } from "../../../models/models";
import FleetGroupHeader from "../FleetGroupHeader";
import {
  MachineryV2CardState,
  MachineryV2Telemetry,
  deriveCardPresentation,
  unconnectedTelemetry,
} from "../cardStates";
import { countStates } from "../fleetSelectors";
import { sectionTitle } from "../fleetGroupBy";
import FleetPhoneRow from "./FleetPhoneRow";
import FleetSortChips from "./FleetSortChips";
import { FleetSortedSection, FleetTableSort, FleetTableSortColumn } from "./fleetTableSort";

export interface FleetTablePhoneViewProps {
  /** [raw group key, the section's machines, the SORTED machines] — built once
   *  by FleetTableView's memo. */
  sorted: readonly FleetSortedSection[];
  states: Map<string, MachineryV2CardState>;
  telemetry: Map<string, MachineryV2Telemetry>;
  newMachineId: string | null;
  collapsedGroups: Set<string>;
  onToggleGroup: (name: string) => void;
  onOpenProfile: (m: Machine) => void;
  sort: FleetTableSort;
  onSortChange: (column: FleetTableSortColumn) => void;
}

const FleetTablePhoneView: React.FC<FleetTablePhoneViewProps> = ({
  sorted, states, telemetry, newMachineId, collapsedGroups, onToggleGroup,
  onOpenProfile, sort, onSortChange,
}) => {
  const { translate } = useTranslation("myMachinery");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0 }}>
      <FleetSortChips sort={sort} onSortChange={onSortChange} />

      {sorted.map(([key, list, rows]) => (
        <div key={key} style={{ minWidth: 0 }}>
          {/* marginBottom 0 — the header bar sits FLUSH on the card below it,
              the same attachment the 1024 table uses. */}
          <FleetGroupHeader
            title={sectionTitle(key, translate)}
            machineCount={list.length}
            counts={countStates(list, states)}
            collapsed={collapsedGroups.has(key)}
            onToggle={() => onToggleGroup(key)}
            marginBottom={0}
            // Always below 1280 here by construction, so the 44px collapse
            // target is a literal rather than a breakpoint read (F10).
            minHeight={44}
          />
          {!collapsedGroups.has(key) && (
            <div
              style={{
                background: "var(--card)", border: "1px solid var(--border)",
                borderRadius: 10, overflow: "hidden", minWidth: 0,
              }}
            >
              {rows.map((m, i) => {
                const t = telemetry.get(m.id) || unconnectedTelemetry();
                return (
                  <FleetPhoneRow
                    key={m.id}
                    machine={m}
                    presentation={deriveCardPresentation(t)}
                    telemetry={t}
                    isNew={m.id === newMachineId}
                    divider={i > 0}
                    onOpenProfile={() => onOpenProfile(m)}
                  />
                );
              })}
            </div>
          )}
        </div>
      ))}

      {/* The board's footnote, REWORDED (F5). It used to promise a "full table
          with columns from 1024px" and was wrong twice: at 1024 the table is
          real but NOT full (PRIPOJ. and 30 DNÍ stay hidden until 1280), and the
          list it sits under now runs to 1023 rather than 767. It names both
          widths for what they are. It is still the ONE place this tier tells the
          user the columns exist somewhere — without it, a dense list reads as
          all there is. */}
      <div
        style={{
          fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--faint)",
          letterSpacing: "0.04em", textAlign: "center", paddingTop: 2,
        }}
      >
        {translate("myMachinery:v2.fleet.table.fullTableHint")}
      </div>
    </div>
  );
};

export default FleetTablePhoneView;
